import { forwardRef, useEffect, useImperativeHandle, useState, type ReactNode } from 'react';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';

/**
 * Generic keyboard-navigable popup list used by the "#" (work items) and "@"
 * (people) triggers. The TipTap Suggestion plugin owns the lifecycle; this
 * module only renders and positions it.
 */

export interface SuggestionListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

export interface SuggestionListProps<T> extends SuggestionProps<T> {
  renderItem: (item: T) => ReactNode;
  emptyText?: string;
}

function SuggestionListInner<T>({ items, command, renderItem, emptyText, loading }: SuggestionListProps<T>, ref: React.Ref<SuggestionListRef>) {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelected(s => (s + items.length - 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelected(s => (s + 1) % Math.max(items.length, 1));
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (items[selected]) command(items[selected]);
        return true;
      }
      return false;
    },
  }));

  if (loading && items.length === 0) return <div className="suggestion-list"><div className="suggestion-empty">Searching…</div></div>;
  if (items.length === 0) return <div className="suggestion-list"><div className="suggestion-empty">{emptyText ?? 'No matches'}</div></div>;

  return (
    <div className="suggestion-list" role="listbox">
      {items.map((item, i) => (
        <button
          key={i}
          type="button"
          role="option"
          aria-selected={i === selected}
          className={`suggestion-item${i === selected ? ' is-selected' : ''}`}
          onMouseEnter={() => setSelected(i)}
          onMouseDown={e => {
            e.preventDefault();
            command(item);
          }}
        >
          {renderItem(item)}
        </button>
      ))}
    </div>
  );
}

export const SuggestionList = forwardRef(SuggestionListInner) as <T>(
  props: SuggestionListProps<T> & { ref?: React.Ref<SuggestionListRef> },
) => ReactNode;

function place(el: HTMLElement, clientRect: (() => DOMRect | null) | null | undefined) {
  const rect = clientRect?.();
  if (!rect) return;
  const margin = 4;
  el.style.left = `${Math.min(rect.left + window.scrollX, window.innerWidth - el.offsetWidth - 8)}px`;
  const below = rect.bottom + window.scrollY + margin;
  const fitsBelow = rect.bottom + el.offsetHeight + margin < window.innerHeight;
  el.style.top = fitsBelow ? `${below}px` : `${rect.top + window.scrollY - el.offsetHeight - margin}px`;
}

/**
 * Builds the `render` function for a Suggestion configuration. The component
 * receives the Suggestion props plus whatever `extraProps` the caller wants
 * (e.g. renderItem) and is mounted in a floating container on <body>.
 */
export function createSuggestionRenderer<T>(
  Component: React.ComponentType<SuggestionListProps<T> & { ref?: React.Ref<SuggestionListRef> }>,
  extraProps: Pick<SuggestionListProps<T>, 'renderItem' | 'emptyText'>,
): SuggestionOptions<T>['render'] {
  return () => {
    let renderer: ReactRenderer<SuggestionListRef, SuggestionListProps<T>> | null = null;
    let container: HTMLDivElement | null = null;

    return {
      onStart: props => {
        renderer = new ReactRenderer(Component, { props: { ...props, ...extraProps }, editor: props.editor });
        container = document.createElement('div');
        container.className = 'suggestion-popup';
        container.appendChild(renderer.element);
        document.body.appendChild(container);
        place(container, props.clientRect);
      },
      onUpdate: props => {
        renderer?.updateProps({ ...props, ...extraProps });
        if (container) {
          container.style.display = '';
          place(container, props.clientRect);
        }
      },
      onKeyDown: props => {
        if (props.event.key === 'Escape') {
          if (container) container.style.display = 'none';
          return true;
        }
        return renderer?.ref?.onKeyDown(props) ?? false;
      },
      onExit: () => {
        renderer?.destroy();
        container?.remove();
        renderer = null;
        container = null;
      },
    };
  };
}
